import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import axios from 'axios';
import { Trophy } from 'lucide-react';
import MfaChallenge from '@/components/MfaChallenge';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function AuthPage({ onLogin }) {
  const [loginData, setLoginData] = useState({ email: '', password: '' });
  const [mfa, setMfa] = useState(null);
  const [registerData, setRegisterData] = useState({
    email: '', password: '', name: '', role: 'assistant_coach', team_id: 'team_new', phone: '', team_code: ''
  });

  const handleLogin = async (e) => {
    e.preventDefault();
    try {
      const response = await axios.post(`${API}/auth/login`, loginData);
      if (response.data.mfa_required) {
        setMfa({ token: response.data.mfa_token, mode: response.data.mfa_enroll_required ? 'enroll' : 'verify' });
        return;
      }
      toast.success('Login successful!');
      onLogin(response.data.user, response.data.token);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Login failed');
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    if (registerData.role === 'assistant_coach' && !registerData.team_code?.trim()) {
      toast.error('A team code is required to join as an assistant coach');
      return;
    }
    try {
      await axios.post(`${API}/auth/register`, registerData);
      const loginRes = await axios.post(`${API}/auth/login`, {
        email: registerData.email, password: registerData.password,
      });
      toast.success('Welcome to Personal AI Coach!');
      onLogin(loginRes.data.user, loginRes.data.token, '/onboarding');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Registration failed');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary/10 via-background to-background flex items-center justify-center p-4 relative overflow-hidden">
      {/* Logo background watermark */}
      <div
        className="absolute inset-0 pointer-events-none opacity-[0.07]"
        style={{
          backgroundImage: "url('/pac-logo.png')",
          backgroundRepeat: 'no-repeat',
          backgroundPosition: 'center',
          backgroundSize: 'min(85vh, 85vw)'
        }}
        data-testid="login-bg-logo"
      />
      <div className="w-full max-w-6xl grid md:grid-cols-2 gap-8 items-center relative z-10">
        <div className="text-center md:text-left space-y-6">
          <div className="flex items-center justify-center md:justify-start gap-3">
            <img
              src="/pac-logo.png"
              alt="PAC - Personal AI Coach"
              className="h-48 w-48 md:h-56 md:w-56 object-contain drop-shadow-lg"
              data-testid="login-logo"
            />
          </div>
          <p className="text-2xl text-muted-foreground">
            Personal AI Coach
          </p>
          <div className="space-y-3 text-left">
            <div className="flex items-center gap-2">
              <div className="h-2 w-2 bg-accent rounded-full"></div>
              <span className="text-sm">Manage team schedules & communications</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="h-2 w-2 bg-accent rounded-full"></div>
              <span className="text-sm">Track player stats & performance</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="h-2 w-2 bg-accent rounded-full"></div>
              <span className="text-sm">Upload & manage game film</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="h-2 w-2 bg-accent rounded-full"></div>
              <span className="text-sm">Automate bookings & meal planning</span>
            </div>
          </div>
        </div>

        <Card className="border-2" data-testid="auth-card">
          <CardHeader>
            <CardTitle className="font-heading text-3xl uppercase">Get Started</CardTitle>
            <CardDescription>Login or create your coaching account</CardDescription>
          </CardHeader>
          <CardContent>
            {mfa ? (
              <MfaChallenge mfaToken={mfa.token} mode={mfa.mode} onCancel={() => setMfa(null)}
                            onSuccess={(user, token) => { toast.success('Login successful!'); onLogin(user, token); }} />
            ) : (
            <>
            <Tabs defaultValue="login" className="w-full">
              <TabsList className="grid w-full grid-cols-2">
                <TabsTrigger value="login" data-testid="login-tab">Login</TabsTrigger>
                <TabsTrigger value="register" data-testid="register-tab">Register</TabsTrigger>
              </TabsList>
              <TabsContent value="login">
                <form onSubmit={handleLogin} className="space-y-4" data-testid="login-form">
                  <div>
                    <Label htmlFor="login-email">Email</Label>
                    <Input
                      id="login-email"
                      type="email"
                      data-testid="login-email"
                      value={loginData.email}
                      onChange={(e) => setLoginData({...loginData, email: e.target.value})}
                      required
                    />
                  </div>
                  <div>
                    <Label htmlFor="login-password">Password</Label>
                    <Input
                      id="login-password"
                      type="password"
                      data-testid="login-password"
                      value={loginData.password}
                      onChange={(e) => setLoginData({...loginData, password: e.target.value})}
                      required
                    />
                  </div>
                  <Button type="submit" className="w-full" data-testid="login-submit-btn">
                    LOGIN
                  </Button>
                </form>
              </TabsContent>
              <TabsContent value="register">
                <form onSubmit={handleRegister} className="space-y-4" data-testid="register-form">
                  <div>
                    <Label htmlFor="register-name">Full Name</Label>
                    <Input
                      id="register-name"
                      data-testid="register-name"
                      value={registerData.name}
                      onChange={(e) => setRegisterData({...registerData, name: e.target.value})}
                      required
                    />
                  </div>
                  <div>
                    <Label htmlFor="register-email">Email</Label>
                    <Input
                      id="register-email"
                      type="email"
                      data-testid="register-email"
                      value={registerData.email}
                      onChange={(e) => setRegisterData({...registerData, email: e.target.value})}
                      required
                    />
                  </div>
                  <div>
                    <Label htmlFor="register-phone">Phone (Optional)</Label>
                    <Input
                      id="register-phone"
                      type="tel"
                      data-testid="register-phone"
                      value={registerData.phone}
                      onChange={(e) => setRegisterData({...registerData, phone: e.target.value})}
                    />
                  </div>
                  <div>
                    <Label htmlFor="register-role">Role</Label>
                    <Select
                      value={registerData.role}
                      onValueChange={(value) => setRegisterData({...registerData, role: value, team_code: ''})}
                    >
                      <SelectTrigger data-testid="register-role">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="head_coach">Head Coach</SelectItem>
                        <SelectItem value="assistant_coach">Assistant Coach</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  {registerData.role === 'assistant_coach' && (
                    <div>
                      <Label htmlFor="register-team-code">Team Code</Label>
                      <Input
                        id="register-team-code"
                        data-testid="register-team-code"
                        placeholder="Enter team code from your head coach"
                        value={registerData.team_code}
                        onChange={(e) => setRegisterData({...registerData, team_code: e.target.value.toUpperCase()})}
                        maxLength={8}
                        required
                        className="font-mono uppercase"
                      />
                      <p className="text-xs text-muted-foreground mt-1">
                        Ask your head coach for their team code to join their team
                      </p>
                    </div>
                  )}
                  <div>
                    <Label htmlFor="register-password">Password</Label>
                    <Input
                      id="register-password"
                      type="password"
                      data-testid="register-password"
                      value={registerData.password}
                      onChange={(e) => setRegisterData({...registerData, password: e.target.value})}
                      required
                    />
                  </div>
                  <Button type="submit" className="w-full" data-testid="register-submit-btn">
                    CREATE ACCOUNT
                  </Button>
                </form>
              </TabsContent>
            </Tabs>

            {/* Divider */}
            <div className="relative my-5">
              <div className="absolute inset-0 flex items-center"><span className="w-full border-t" /></div>
              <div className="relative flex justify-center text-xs uppercase">
                <span className="bg-card px-2 text-muted-foreground">or</span>
              </div>
            </div>

            {/* Sign in with Google (Emergent managed) */}
            <Button
              type="button"
              variant="outline"
              className="w-full"
              data-testid="google-signin-btn"
              onClick={() => {
                // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
                const redirectUrl = window.location.origin + '/';
                window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
              }}
            >
              <svg className="h-4 w-4 mr-2" viewBox="0 0 24 24" aria-hidden="true">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                <path fill="#FBBC05" d="M5.84 14.1c-.22-.66-.35-1.36-.35-2.1s.13-1.44.35-2.1V7.06H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.94l3.66-2.84z" />
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.06l3.66 2.84c.87-2.6 3.3-4.52 6.16-4.52z" />
              </svg>
              Continue with Google
            </Button>
            </>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}